import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';

// PrimeNG
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { MessageModule } from 'primeng/message';
import { TagModule } from 'primeng/tag';
import { DividerModule } from 'primeng/divider';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService, MenuItem } from 'primeng/api';

// Kanzi
import { KanziUiModule } from '@kanzi-apes/kanzi-ui';
import { UbicacionesQueriesService } from '../../../queries/ubicaciones-queries.service';

/**
 * Ubicación Detail Component
 * 
 * Display full ubicación details with hierarchical path (breadcrumb)
 */
@Component({
    selector: 'kanzi-apes-ubicacion-detail',
    imports: [
        CommonModule,
        TranslateModule,
        CardModule,
        ButtonModule,
        SkeletonModule,
        MessageModule,
        TagModule,
        DividerModule,
        BreadcrumbModule,
        ConfirmDialogModule,
        KanziUiModule
    ],
    providers: [ConfirmationService, MessageService],
    templateUrl: './ubicacion-detail.component.html',
    styleUrls: ['./ubicacion-detail.component.scss']
})
export class UbicacionDetailComponent {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private queries = inject(UbicacionesQueriesService);
  private confirmService = inject(ConfirmationService);
  private messageService = inject(MessageService);

  // Zone type labels mapping
  readonly zoneTypeLabels: Record<string, string> = {
    RECEPTION: 'Recepción',
    EXCHANGE_RECEPTION: 'Recepción de Intercambio',
    STORAGE: 'Almacenamiento',
    BUFFER_STORAGE: 'Almacenamiento de Buffer',
    TRANSIT: 'Zona de Tránsito',
    DISPATCH: 'Despacho',
    EXCHANGE_DISPATCH: 'Despacho de Intercambio',
    CONTROL: 'Control',
    LOSS_CONTROL: 'Control de Pérdidas',
    DESTRUCTION: 'Destrucción',
    DONATION: 'Donación',
  };

  // Route params
  ubicacionId = signal<number | null>(null);
  isQueryEnabled = computed(() => this.ubicacionId() !== null);

  // Queries
  ubicacionQuery = this.queries.useUbicacionDetail(this.ubicacionId, this.isQueryEnabled);
  deleteMutation = this.queries.useDeleteUbicacion();

  // Computed ubicacion data
  ubicacion = computed(() => {
    if (this.ubicacionQuery.isPending() || this.ubicacionQuery.isFetching()) {
      return null;
    }
    return this.ubicacionQuery.data();
  });

  // Loading states
  isLoading = computed(() => this.ubicacionQuery.isPending() || this.ubicacionQuery.isFetching());
  isDeleting = computed(() => this.deleteMutation.isPending());

  constructor() {
    // Get ID from route
    this.route.params.subscribe(params => {
      const id = params['id'];
      if (id) {
        this.ubicacionId.set(parseInt(id, 10));
      }
    });
  }

  /**
   * Navigate to edit page
   */
  onEdit(): void {
    const id = this.ubicacionId();
    if (id) {
      this.router.navigate(['/locations/ubicaciones/edit', id]);
    }
  }

  /**
   * Delete ubicación with confirmation
   */
  onDelete(): void {
    const ubi = this.ubicacion();
    if (!ubi || !ubi.id) return;

    this.confirmService.confirm({
      message: `¿Está seguro que desea eliminar la ubicación "${ubi.name}"?`,
      header: 'Confirmar eliminación',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, eliminar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.deleteMutation.mutate(ubi.id!, {
          onSuccess: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Éxito',
              detail: 'Ubicación eliminada correctamente'
            });
            setTimeout(() => {
              this.router.navigate(['/locations/ubicaciones/list']);
            }, 1000);
          },
          onError: (error: any) => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: error?.message || 'Error al eliminar la ubicación'
            });
          }
        });
      }
    });
  }

  /**
   * Go back to list
   */
  onBack(): void {
    this.router.navigate(['/locations/ubicaciones/list']);
  }
}
